(() => {
  const API_BASE_URL = 'https://data.meyercookware.com.au/stockists';
  const AUSTRALIA_CENTER = { lat: -28.258198573777268, lng: 134.51686391190657 };

  let stockistMap = null;
  let stockistMapMarkers = [];
  let stockistMapInfoWindow = null;

  const getElement = (id) => document.getElementById(id);

  const getQuerySettings = () => {
    const query = new URLSearchParams(window.location.search);
    const type = (query.get('type') || '').trim().toLowerCase();

    return {
      range: (query.get('range') || '').trim(),
      type: ['stores', 'online'].includes(type) ? type : '',
      location: (query.get('location') || '').trim(),
      hasSearchIntent: query.has('range') || query.has('type') || query.has('location'),
    };
  };

  const setSelectValue = (select, value) => {
    if (!value) return false;

    const normalizedValue = value.toLowerCase();
    const matchingOption = Array.from(select.options).find((option) => option.value.toLowerCase() === normalizedValue);

    if (!matchingOption) return false;

    select.value = matchingOption.value;
    return true;
  };

  const setLoading = (isLoading) => {
    const searchButton = getElement('stockist-search');

    getElement('stockists-loading').style.display = isLoading ? 'block' : 'none';
    getElement('stockists').style.display = isLoading ? 'none' : 'grid';
    getElement('stockists-empty').style.display = 'none';
    searchButton.disabled = isLoading;
    searchButton.querySelector('span').textContent = isLoading ? 'Searching...' : 'Find stockists';
  };

  const clearResults = () => {
    getElement('stockists-results').replaceChildren();
    stockistMapMarkers.forEach((marker) => marker.setMap(null));
    stockistMapMarkers = [];
  };

  const setFinderMode = () => {
    const isOnline = getElement('stockist-type').value === 'online';
    getElement('stockist-finder').classList.toggle('is-online', isOnline);
    getElement('stockists').classList.toggle('is-online', isOnline);
    getElement('stockist-location').toggleAttribute('required', !isOnline);
  };

  const setResultsHeading = (title, kicker) => {
    getElement('stockists-results-title').textContent = title;
    getElement('stockists-results-kicker').textContent = kicker;
  };

  const fetchJson = async (url) => {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return response.json();
  };

  const appendText = (parent, text) => {
    parent.appendChild(document.createTextNode(text || ''));
  };

  const appendLine = (parent, text) => {
    if (text) {
      appendText(parent, text);
    }
    parent.appendChild(document.createElement('br'));
  };

  const escapeHtml = (value) => {
    const wrapper = document.createElement('div');
    wrapper.textContent = value || '';
    return wrapper.innerHTML;
  };

  const createSafeLink = (url) => {
    if (!url) return null;

    try {
      const parsedUrl = new URL(url, window.location.origin);

      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        return null;
      }

      const link = document.createElement('a');
      link.href = parsedUrl.href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = url;
      return link;
    } catch (error) {
      return null;
    }
  };

  const createErrorMessage = (message) => {
    const error = document.createElement('p');
    error.className = 'stockist-error';
    error.textContent = message;
    return error;
  };

  const createMarkerIcon = (index, isActive = false) => {
    const fillColor = isActive ? '#1c1b1b' : '#f15a30';
    const strokeColor = isActive ? '#f15a30' : '#ffffff';
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="52" height="64" viewBox="0 0 52 64">
        <path fill="${fillColor}" stroke="${strokeColor}" stroke-width="4" d="M26 2C12.75 2 2 12.75 2 26c0 17.5 24 36 24 36s24-18.5 24-36C50 12.75 39.25 2 26 2Z"/>
        <circle cx="26" cy="26" r="15" fill="#fff"/>
        <text x="26" y="31" text-anchor="middle" font-family="Arial, sans-serif" font-size="16" font-weight="700" fill="${fillColor}">${index}</text>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(isActive ? 46 : 40, isActive ? 56 : 49),
      anchor: new google.maps.Point(isActive ? 23 : 20, isActive ? 56 : 49),
    };
  };

  const createOnlineStockist = (stockist) => {
    const wrapper = document.createElement('div');
    const name = document.createElement('p');
    const actions = document.createElement('div');
    const link = createSafeLink(stockist.website);

    wrapper.className = 'stockist';
    name.textContent = stockist.store_name || '';
    wrapper.appendChild(name);

    if (link) {
      actions.className = 'stockist__actions';
      link.textContent = 'Visit website';
      link.setAttribute('aria-label', `Visit ${stockist.store_name || 'stockist'} website`);
      actions.appendChild(link);
      wrapper.appendChild(actions);
    }

    return wrapper;
  };

  const createDirectionsLink = (stockist) => {
    const address = [
      stockist.store_name,
      stockist.address_line_1,
      stockist.address_line_2,
      stockist.suburb,
      stockist.state,
      stockist.postcode,
    ].filter(Boolean).join(' ');

    if (!address) return null;

    const link = document.createElement('a');
    link.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Directions';
    link.setAttribute('aria-label', `Get directions to ${stockist.store_name || 'stockist'}`);
    return link;
  };

  const createStoreStockist = (stockist, index) => {
    const wrapper = document.createElement('div');
    const name = document.createElement('p');
    const number = document.createElement('span');
    const details = document.createElement('p');
    const actions = document.createElement('div');
    const phone = document.createElement('strong');
    const link = createSafeLink(stockist.website);
    const directionsLink = createDirectionsLink(stockist);

    wrapper.className = 'stockist';
    wrapper.tabIndex = 0;
    wrapper.setAttribute('role', 'button');
    wrapper.setAttribute('aria-label', `Show ${stockist.store_name || 'stockist'} on the map`);
    wrapper.dataset.stockistIndex = String(index);
    number.className = 'stockist__number';
    number.textContent = index;
    name.appendChild(number);
    appendText(name, stockist.store_name || '');
    wrapper.appendChild(name);

    details.className = 'stockist__details';
    appendLine(details, stockist.address_line_1);
    appendLine(details, stockist.address_line_2);
    appendLine(details, stockist.suburb);
    appendLine(details, [stockist.state, stockist.postcode].filter(Boolean).join(' '));
    wrapper.appendChild(details);

    actions.className = 'stockist__actions';

    if (stockist.phone) {
      const phoneLink = document.createElement('a');
      const phoneHref = stockist.phone.replace(/[^\d+]/g, '');
      phoneLink.href = `tel:${phoneHref}`;
      phone.textContent = stockist.phone;
      phoneLink.appendChild(phone);
      actions.appendChild(phoneLink);
    }

    if (link) {
      link.textContent = 'Visit website';
      link.setAttribute('aria-label', `Visit ${stockist.store_name || 'stockist'} website`);
      actions.appendChild(link);
    }

    if (directionsLink) {
      actions.appendChild(directionsLink);
    }

    if (actions.hasChildNodes()) {
      wrapper.appendChild(actions);
    }

    wrapper.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;

      focusStockistMarker(index);
    });

    wrapper.addEventListener('keydown', (event) => {
      if (event.target.closest('a')) return;
      if (!['Enter', ' '].includes(event.key)) return;

      event.preventDefault();
      focusStockistMarker(index);
    });

    return wrapper;
  };

  const getStockistList = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload.stockists)) return payload.stockists;
    if (Array.isArray(payload)) return payload;

    return Object.values(payload).flatMap((value) => Array.isArray(value) ? value : []);
  };

  window.initialiseStockistMap = () => {
    if (!window.google?.maps || !getElement('stockists-map')) return;

    stockistMap = new google.maps.Map(getElement('stockists-map'), {
      zoom: 4,
      center: AUSTRALIA_CENTER,
      clickableIcons: true,
      disableDoubleClickZoom: false,
      draggable: true,
      fullscreenControl: true,
      gestureHandling: 'greedy',
      keyboardShortcuts: true,
      mapTypeControl: false,
      scrollwheel: true,
      streetViewControl: false,
      zoomControl: true,
    });

    stockistMapInfoWindow = new google.maps.InfoWindow();
  };

  const centerStockistMap = () => {
    if (!stockistMap || stockistMapMarkers.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    stockistMapMarkers.forEach((marker) => bounds.extend(marker.getPosition()));
    stockistMap.fitBounds(bounds);
  };

  const refreshStockistMap = () => {
    if (!stockistMap || !window.google?.maps) return;

    window.requestAnimationFrame(() => {
      google.maps.event.trigger(stockistMap, 'resize');
      centerStockistMap();
    });
  };

  const highlightStockist = (index, shouldScroll = true) => {
    document.querySelectorAll('.stockist.is-active').forEach((stockist) => stockist.classList.remove('is-active'));

    const stockist = document.querySelector(`.stockist[data-stockist-index="${index}"]`);
    if (!stockist) return;

    stockist.classList.add('is-active');
    if (shouldScroll) {
      stockist.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const getMarkerByIndex = (index) => stockistMapMarkers.find((marker) => marker.stockistIndex === index);

  const setActiveMarker = (index) => {
    stockistMapMarkers.forEach((marker) => {
      marker.setIcon(createMarkerIcon(marker.stockistIndex, marker.stockistIndex === index));
      marker.setZIndex(marker.stockistIndex === index ? 1000 : marker.stockistIndex);
    });
  };

  const openMarkerInfoWindow = (marker) => {
    if (!stockistMapInfoWindow || !marker.stockist) return;

    const details = [
      marker.stockist.address_line_1,
      marker.stockist.address_line_2,
      marker.stockist.suburb,
      [marker.stockist.state, marker.stockist.postcode].filter(Boolean).join(' '),
    ].filter(Boolean).map(escapeHtml).join('<br>');

    stockistMapInfoWindow.setContent(`
      <div class="stockist-map-info">
        <strong>${escapeHtml(marker.stockist.store_name || 'Stockist')}</strong>
        ${details ? `<p>${details}</p>` : ''}
      </div>
    `);
    stockistMapInfoWindow.open({ map: stockistMap, anchor: marker });
  };

  const focusStockistMarker = (index, shouldScrollCard = false) => {
    const marker = getMarkerByIndex(index);
    if (!stockistMap || !marker) return;

    const position = marker.getPosition();
    highlightStockist(index, shouldScrollCard);
    setActiveMarker(index);
    stockistMap.panTo(position);

    if ((stockistMap.getZoom() || 0) < 13) {
      stockistMap.setZoom(13);
    }

    marker.setAnimation(google.maps.Animation.BOUNCE);
    window.setTimeout(() => marker.setAnimation(null), 900);
    openMarkerInfoWindow(marker);
  };

  const createMarker = (stockist, index) => {
    if (!stockistMap || !window.google?.maps || !stockist.gps_latitude || !stockist.gps_longitude) return;

    const marker = new google.maps.Marker({
      animation: google.maps.Animation.DROP,
      position: new google.maps.LatLng(stockist.gps_latitude, stockist.gps_longitude),
      map: stockistMap,
      icon: createMarkerIcon(index),
      optimized: false,
      title: stockist.store_name || `Stockist ${index}`,
      zIndex: index,
    });

    marker.stockistIndex = index;
    marker.stockist = stockist;
    marker.addListener('click', () => focusStockistMarker(index, true));
    stockistMapMarkers.push(marker);
  };

  const loadRanges = async () => {
    const rangeSelect = getElement('stockist-range');

    try {
      const ranges = await fetchJson(`${API_BASE_URL}/ranges?brand=Meyer`);

      rangeSelect.replaceChildren(new Option('All Ranges', ''));
      ranges.forEach((range) => rangeSelect.appendChild(new Option(range, range)));
      rangeSelect.disabled = false;
    } catch (error) {
      rangeSelect.replaceChildren(new Option('All Ranges', ''));
      rangeSelect.disabled = false;
    }
  };

  const applyQuerySettings = (querySettings) => {
    if (querySettings.type) {
      getElement('stockist-type').value = querySettings.type;
    }

    setSelectValue(getElement('stockist-range'), querySettings.range);
    getElement('stockist-location').value = querySettings.location;
    setFinderMode();
  };

  const shouldExecuteQuerySearch = (querySettings) => {
    if (!querySettings.hasSearchIntent) return false;

    const selectedType = getElement('stockist-type').value;
    return selectedType === 'online' || Boolean(getElement('stockist-location').value.trim());
  };

  const findOnlineStockists = async () => {
    const results = getElement('stockists-results');
    const range = getElement('stockist-range').value;
    const payload = await fetchJson(`${API_BASE_URL}/online?brand=Meyer&range=${encodeURIComponent(range)}`);
    const seenWebsites = new Set();
    const stockists = getStockistList(payload);

    stockists.forEach((stockist) => {
      const website = stockist.website || '';

      if (website && seenWebsites.has(website)) return;
      if (website) seenWebsites.add(website);

      results.appendChild(createOnlineStockist(stockist));
    });

    if (!results.hasChildNodes()) {
      results.appendChild(createErrorMessage('Unfortunately there are no online stockists for that range.'));
      setResultsHeading('Online stockists', 'No matches found');
    } else {
      setResultsHeading('Online stockists', `${results.children.length} available`);
    }
  };

  const findStoreStockists = async () => {
    if (!window.google?.maps) {
      throw new Error('Google Maps has not loaded yet.');
    }

    const results = getElement('stockists-results');
    const geocoder = new google.maps.Geocoder();
    const range = getElement('stockist-range').value;
    let location = getElement('stockist-location').value.trim();

    if (!location) {
      results.appendChild(createErrorMessage('Please enter a suburb or postcode to find nearby stores.'));
      setResultsHeading('Nearby stores', 'Location required');
      return;
    }

    if (!Number.isNaN(Number(location)) && location !== '') {
      location = `Postcode ${location}`;
    }

    const geocodeResult = await new Promise((resolve, reject) => {
      geocoder.geocode({ address: location, componentRestrictions: { country: 'AU' } }, (geocodeResults, status) => {
        if (status === 'OK' && geocodeResults?.[0]) {
          resolve(geocodeResults[0]);
        } else {
          reject(new Error('Geocode failed.'));
        }
      });
    });

    const lat = geocodeResult.geometry.location.lat();
    const lng = geocodeResult.geometry.location.lng();
    const payload = await fetchJson(`${API_BASE_URL}/stores?brand=Meyer&range=${encodeURIComponent(range)}&lat=${lat}&long=${lng}`);
    const stockists = getStockistList(payload);

    window.initialiseStockistMap();

    if (stockists.length === 0) {
      results.appendChild(createErrorMessage('Unfortunately there are no stores nearby at that location.'));
      setResultsHeading('Nearby stores', 'No matches found');
      return;
    }

    stockists.forEach((stockist, index) => {
      const displayIndex = index + 1;
      results.appendChild(createStoreStockist(stockist, displayIndex));
      createMarker(stockist, displayIndex);
    });

    setResultsHeading('Nearby stores', `${stockists.length} closest matches`);
    centerStockistMap();
  };

  const executeFind = async () => {
    const isStoreSearch = getElement('stockist-type').value === 'stores';

    clearResults();
    setResultsHeading(isStoreSearch ? 'Nearby stores' : 'Online stockists', 'Searching');
    setLoading(true);

    try {
      if (isStoreSearch) {
        await findStoreStockists();
      } else {
        await findOnlineStockists();
      }
    } catch (error) {
      if (isStoreSearch) {
        window.initialiseStockistMap();
        getElement('stockists-results').appendChild(createErrorMessage("We're having some trouble with that location, please double check you've entered it correctly and try again."));
        setResultsHeading('Nearby stores', 'Search error');
      } else {
        getElement('stockists-results').appendChild(createErrorMessage("We're having some trouble loading online stockists, please try again."));
        setResultsHeading('Online stockists', 'Search error');
      }
    } finally {
      setLoading(false);
      if (isStoreSearch) {
        refreshStockistMap();
      }
    }
  };

  document.addEventListener('DOMContentLoaded', async () => {
    const querySettings = getQuerySettings();

    await loadRanges();
    applyQuerySettings(querySettings);

    setFinderMode();
    getElement('stockist-type').addEventListener('change', setFinderMode);
    getElement('stockist-location').addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        executeFind();
      }
    });
    getElement('stockist-search').addEventListener('click', executeFind);

    if (shouldExecuteQuerySearch(querySettings)) {
      executeFind();
    }
  });
})();
